import type { RuleNodeType } from "../types/nodes";

/**
 * Node-adjacency matrix for the "Add elements to canvas" dropdown.
 *
 * Keys are the SOURCE node ("node on screen" — the node you're adding after);
 * values are the node types offered in the dropdown for that source. A type
 * omitted from a source's list is hidden from the dropdown when adding after
 * that node. Source the rules from the product matrix:
 *
 *   - `reaction_location` (the entry node) is never itself addable, but a fresh
 *     set of steps can follow it.
 *   - `query_builder` is the node shown in the dropdown as "Database Table
 *     Variable" / Query Builder.
 *   - `decision` is the only branch node that may be followed by a `stop`
 *     (alongside a `reaction`).
 *   - `reaction` is the only source after which `clear_reaction` and `stop`
 *     appear. `clear_reaction` itself can only be followed by another reaction
 *     or a stop (so Clear Reaction only ever surfaces after a Reaction).
 *   - `stop` is terminal — nothing follows it.
 *
 * `ghost` never acts as a menu source (decision-branch placeholders open the
 * menu against the parent decision), so it is intentionally empty.
 */
export const ADD_NODE_ADJACENCY: Record<RuleNodeType, RuleNodeType[]> = {
	reaction_location: ["query_builder", "loop", "assignment", "filter", "sort", "decision", "reaction"],
	query_builder:     ["query_builder", "loop", "assignment", "filter", "sort", "decision", "reaction"],
	assignment:        ["query_builder", "loop", "assignment", "filter", "sort", "decision", "reaction"],
	filter:            ["query_builder", "loop", "assignment", "filter", "sort", "decision", "reaction"],
	sort:              ["query_builder", "loop", "assignment", "filter", "sort", "decision", "reaction"],
	loop:              ["assignment", "filter", "sort", "decision", "reaction"],
	decision:          ["query_builder", "loop", "assignment", "filter", "sort", "decision", "reaction", "stop"],
	reaction:          ["reaction", "clear_reaction", "stop"],
	clear_reaction:    ["reaction", "stop"],
	stop:              [],
	ghost:             [],
};

/**
 * Node types a given source may be followed by, per {@link ADD_NODE_ADJACENCY}.
 * Returns `null` when the source type is unknown (or undefined) so callers can
 * fall back to showing every option rather than hiding the whole menu.
 */
export const getAllowedNextNodeTypes = (sourceType?: string | null): RuleNodeType[] | null => {
	if (!sourceType) return null;
	return ADD_NODE_ADJACENCY[sourceType as RuleNodeType] ?? null;
};
